import React from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import cn from 'classnames'
import arrayMutators from 'final-form-arrays'
import { useSelector } from 'react-redux'
import { FieldArray } from 'react-final-form-arrays'
import dayjs from '../../utilities/dayjs'
import notify from '../../utilities/notify'
import useMutation from '../../hooks/useMutation'
import { TextInput } from '../TextInput'
import { DatePickerInput } from '../DatePickerInput'
import { TimeInput } from '../TimeInput'
import styles from './new-requests.module.scss'
import { SelectInput } from '../SelectInput'
import useQuery from '../../hooks/useQuery'
import QUERY_KEYS from '../../graphql/queryKeys'
import resourceTypesQuery from '../../graphql/queries/resourceTypes'
import updateServiceRequestMutation from '../../graphql/mutations/updateServiceRequest'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import { Spinner } from '../../pages/shared/Spinner'
import { formatPhoneNumber } from '../../utilities/stringUtilities'

export default function ServiceRequestModify ({
  request,
  toggleDeclineForm,
  handleConfirm,
  canConfirm,
  refetchServiceRequests
}) {
  const { user } = useSelector(({ user }) => ({ user: user.user }))
  const { data: { resourceTypes }, isFetching: isFetchingResourceTypes } = useQuery(
    [QUERY_KEYS.resourceTypes, { haulerId: user.haulerId, includeCounts: false, includeExtra: false }],
    resourceTypesQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve resource types')
      },
      placeholderData: { resourceTypes: {} }
    }
  )

  const { mutate: updateServiceRequest } = useMutation(updateServiceRequestMutation, {
    onSuccess (data) {
      const [service] = data
      const whatUp = service.status === 'DECLINED' ? 'declined' : 'updated'
      const accountName = [service.firstName, service.lastName].filter(Boolean).join(' ')
      notify('success', `Order for ${accountName} ${whatUp}`)
      toggleDeclineForm(false)
      refetchServiceRequests()
    },
    onError (error) {
      captureErrorAndNotify(error, 'Failed to update order')
    }
  })

  const service = request[0]
  const ticketData = request.map(r => r.resourceType.id)
  const initialValues = {
    serviceDate: service.serviceDate,
    selectedProducts: ticketData,
    requestedStartTime: dayjs.utc(service.requestedStartTime).format('HH:mm'),
    requestedEndTime: dayjs.utc(service.requestedEndTime).format('HH:mm'),
    issueNotes: service.issueNotes
  }

  function onCancelModify () {
    toggleDeclineForm()
  }

  function onDecline (formValues) {
    if (formValues.issueNotes == null || formValues.issueNotes === '') {
      notify('error', 'Please enter a reason why this service was declined.', 6)
      return
    }

    updateServiceRequest({
      orderId: service.orderId,
      status: 'DECLINED',
      issueNotes: formValues.issueNotes
    })
  }

  function onModify (formValues) {
    const updatableKeys = ['serviceDate', 'requestedStartTime', 'requestedEndTime']
    const updateValues = {
      orderId: service.orderId,
      status: 'PENDING_RESOLUTION',
      issueNotes: formValues.issueNotes
    }

    updatableKeys.forEach(key => {
      if (initialValues[key] !== formValues[key]) {
        updateValues[key] = formValues[key]
      }
    })

    const productInfo = formValues.selectedProducts.map((product, idx) => {
      if (product === initialValues.selectedProducts[idx]) return null

      return {
        serviceId: request[idx].id,
        newAssetId: product ?? null
      }
    }).filter(p => p != null)

    if (productInfo.length > 0) updateValues.productInfo = productInfo

    if (formValues.issueNotes == null || formValues.issueNotes === '') {
      notify('error', 'Please enter a reason why this service was modified.', 6)
      return
    }

    updateServiceRequest(updateValues)
  }

  function handleSubmit () {
    handleConfirm()
  }

  function validate (values) {
    const errors = {}

    if (values.requestedStartTime && !values.requestedEndTime) {
      errors.requestedEndTime = 'Required if Start Time'
    }

    if (values.requestedEndTime && !values.requestedStartTime) {
      errors.requestedStartTime = 'Required if End Time'
    }

    const formattedRequestedStartTime = dayjs(values.requestedStartTime, 'HH:mm')
    const formattedRequestedEndTime = dayjs(values.requestedEndTime, 'HH:mm')
    const invalidTimes =
      values.requestedStartTime &&
      values.requestedEndTime &&
      formattedRequestedEndTime.isBefore(formattedRequestedStartTime)
    const sameTimes =
      values.requestedStartTime &&
      values.requestedEndTime &&
      formattedRequestedEndTime.isSame(formattedRequestedStartTime)

    if (invalidTimes || sameTimes) {
      errors.requestedEndTime = 'End Time must be after Start Time'
      errors.requestedStartTime = 'Start Time must be before End Time'
    }

    if (!values.serviceDate) {
      errors.serviceData = 'Service date is required'
    }

    return errors
  }

  function buildAssetOptions () {
    return resourceTypes.reduce((acc, rt) => {
      acc[rt.id] = `${rt.name} (${rt.shortCode})`
      return acc
    }, {})
  }

  if (isFetchingResourceTypes) {
    return (
      <Spinner isFetching />
    )
  }

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit} validate={validate} mutators={{ ...arrayMutators }}>
      {({ form, handleSubmit, values, invalid, pristine }) => (
        <form onSubmit={handleSubmit} autoComplete='off' className={cn(styles.modifyConfirmModalContainer)}>
          <h3>Modify/Decline Service</h3>
          <h4>Contact Information</h4>
          <p>{service.firstName} {service.lastName}</p>
          <p>{formatPhoneNumber(service.primaryPhone)}</p>
          <p>{service.email}</p>
          <h4>Service Information</h4>
          <p>{service.addressline1}, {service.city}, {service.state} {service.zip}</p>
          {service.addressDetails && <p>Details: {service.addressDetails}</p>}
          <div>
            <Field
              label='Service Date'
              name='serviceDate'
              placeholder='Date'
              component={DatePickerInput}
            />
            <Field
              name='requestedStartTime'
              label='Customer Requested Start Time'
              component={TimeInput}
            />
            <Field
              name='requestedEndTime'
              label='Customer Requested End Time'
              component={TimeInput}
            />
          </div>

          <div>
            Asset(s) Requested
          </div>
          <FieldArray name='selectedProducts'>
            {({ fields }) => (
              <div>
                { fields.map((fieldName, idx) => (
                  <Field
                    key={idx}
                    name={fieldName}
                    component={SelectInput}
                    options={buildAssetOptions()}
                  />
                )) }
              </div>
            )}
          </FieldArray>
          <div>
            <Field
              label='Reason to Modify Request'
              placeholder='Ex: Inventory, Schedule full, Asset Change'
              name='issueNotes'
              component={TextInput}
            />
          </div>

          <div className={cn(styles.serviceRequestModifyButtons)}>
            <button type='button'
              className={cn('dis-btn dis-btn-lg dis-btn-white')}
              onClick={onCancelModify}>
              Cancel Modify
            </button>
            <button type='button'
              className={cn('dis-btn dis-btn-lg dis-btn-danger')}
              onClick={() => onDecline(values)}>
              Decline Service
            </button>
            <button type='button'
              className={cn('dis-btn dis-btn-lg dis-btn-primary')}
              disabled={invalid || pristine}
              onClick={() => onModify(values)}>
              Modify Service
              <i className='material-icons dis-btn-icon'>edit</i>
            </button>
            {canConfirm && (
              <button type='submit'
                className={cn('dis-btn dis-btn-lg dis-btn-primary')}
                disabled={!pristine}>
                Confirm Service
                <i className='material-icons dis-btn-icon'>check</i>
              </button>
            )}
          </div>
        </form>
      )}
    </Form>
  )
}

ServiceRequestModify.propTypes = {
  request: PropTypes.array.isRequired,
  toggleDeclineForm: PropTypes.func.isRequired,
  handleConfirm: PropTypes.func.isRequired,
  canConfirm: PropTypes.bool.isRequired,
  refetchServiceRequests: PropTypes.func.isRequired
}
